import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // verifying, success, already_verified, error
  const [message, setMessage] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [showResendForm, setShowResendForm] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setStatus('error');
      setMessage('Invalid verification link');
      return;
    }

    // Call verification endpoint
    axios.post(`${process.env.REACT_APP_API_URL}/api/auth/verify-email`, { token })
      .then(() => {
        setStatus('success');
        setMessage('Email verified successfully!');
      })
      .catch((error) => {
        const errorDetail = error.response?.data?.detail || '';
        // Check if the error indicates the token was already used (email already verified)
        if (errorDetail.toLowerCase().includes('invalid') || errorDetail.toLowerCase().includes('expired')) {
          // Token invalid could mean already used - show friendly message
          setStatus('already_verified');
          setMessage('This verification link has already been used or expired. If you completed verification, your email should be verified.');
        } else {
          setStatus('error');
          setMessage(errorDetail || 'Verification failed. Please try again.');
        }
      });
  }, [searchParams]);

  const handleResendVerification = async (e) => {
    e.preventDefault();
    if (!resendEmail) {
      setResendMessage('Please enter your email address');
      return;
    }

    setResending(true);
    setResendMessage('');

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/resend-verification`, {
        email: resendEmail
      });
      setResendMessage('Verification email sent! Please check your inbox and spam folder.');
      setShowResendForm(false);
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      if (errorDetail === 'Email already verified') {
        setResendMessage('Your email is already verified. You can log in now.');
      } else {
        setResendMessage(errorDetail || 'Failed to send verification email. Please try again.');
      }
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Email Verification
          </h2>
        </div>

        <div className="mt-8 bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {status === 'verifying' && (
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Verifying your email...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Email Verified!</h2>
              <p className="text-gray-600 mb-4">Your email has been verified successfully.</p>
              <p className="text-gray-500 text-sm">You can now close this window and continue in your original browser tab.</p>
            </div>
          )}

          {status === 'already_verified' && (
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Email Already Verified!</h2>
              <p className="text-gray-600 mb-4">This verification link has already been used. Your email is verified and you're all set.</p>
              <p className="text-gray-500 text-sm">You can now close this window and continue in your original browser tab.</p>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <p className="mt-4 text-red-600">{message}</p>

              <div className="mt-6 space-y-4">
                {!showResendForm ? (
                  <button
                    onClick={() => setShowResendForm(true)}
                    className="text-indigo-600 hover:text-indigo-500 block w-full"
                  >
                    Resend verification email
                  </button>
                ) : (
                  <form onSubmit={handleResendVerification} className="space-y-3">
                    <div>
                      <label htmlFor="email" className="sr-only">Email address</label>
                      <input
                        id="email"
                        type="email"
                        value={resendEmail}
                        onChange={(e) => setResendEmail(e.target.value)}
                        placeholder="Enter your email address"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                        required
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="submit"
                        disabled={resending}
                        className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm font-medium"
                      >
                        {resending ? 'Sending...' : 'Send Verification Email'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowResendForm(false);
                          setResendMessage('');
                        }}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}

                {resendMessage && (
                  <p className={`text-sm ${
                    resendMessage.includes('sent') || resendMessage.includes('already verified')
                      ? 'text-green-600'
                      : 'text-red-600'
                  }`}>
                    {resendMessage}
                  </p>
                )}

                <Link
                  to="/login"
                  className="text-indigo-600 hover:text-indigo-500 block"
                >
                  Back to login
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
